import { canDo, canOpenScreen, MODULES_BY_KEY, type ModuleKey } from '@ruby/shared';
import { useNavigate, useParams, useRouterState, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { Construction, Eye, Pencil } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { ViewOnlyContext } from '@/lib/permissions';
import { MODULE_UI } from '@/app/modules';
import { Card, EmptyState, PageHeader } from '@/components/ui/misc';
import { useSession } from '@/lib/session';
import { previousEntry, recordVisit } from '@/lib/nav-context';
import { Forbidden, NotFound } from './status-pages';

/**
 * Resolves /m/:module/:screen[/:id] to the module's registered screen, enforcing view permission.
 * A record of another screen opens inside the screen it was opened from (/m/:module/:screen/:id?doc=other.screen),
 * so the menu, breadcrumb and Back stay in the user's working context (see lib/nav-context). Its other parameters
 * (a new receipt's ?invoice=…, a new payment's ?bills=…) go with it.
 */
export function ScreenHost({ kind }: { kind: 'list' | 'detail' }) {
  const { module, screen, id } = useParams({ strict: false }) as { module: ModuleKey; screen: string; id?: string };
  const search = useSearch({ strict: false }) as { doc?: string; mode?: string } & Record<string, unknown>;
  const { doc, mode } = search;
  const viewOnly = kind === 'detail' && mode === 'view';
  const navigate = useNavigate();
  const perms = useSession((s) => s.boot?.permissions) ?? {};
  // "Submit & new" navigates with a fresh token so the form starts blank again.
  const location = useRouterState({ select: (s) => s.location });
  const fresh = (location.state as { fresh?: number } | undefined)?.fresh ?? 0;
  // Opened from another screen's list or record: stay in that screen, hosting this record. Worked out once per page
  // visit, and only while the address is still this page's: while Back is leaving it, the page renders once more with
  // the router already one step back — re-asking then saw the page before the opener and moved this record into that
  // screen, replacing the list in the history (Back went two pages, owner 2026-10-28).
  const visit = (location.state as { __TSR_key?: string } | undefined)?.__TSR_key ?? location.href;
  const onThisPage = decodeURI(location.pathname) === (kind === 'detail' ? `/m/${module}/${screen}/${id}` : `/m/${module}/${screen}`);
  const from = useMemo(() => (kind === 'detail' && !doc && onThisPage ? previousEntry() : null), [visit]); // eslint-disable-line react-hooks/exhaustive-deps
  const rehome = !!from && (from.module !== module || from.screen !== screen) && !!MODULE_UI[from.module as ModuleKey]?.screens?.[from.screen];
  useEffect(() => {
    if (!onThisPage) return;
    if (rehome) void navigate({ to: '/m/$module/$screen/$id', params: { module: from!.module, screen: from!.screen, id: id! }, search: { ...search, doc: `${module}.${screen}` } as never, state: location.state as never, replace: true });
    else recordVisit({ module, screen, kind });
  }, [rehome, module, screen, kind, id, doc, visit]); // eslint-disable-line react-hooks/exhaustive-deps
  if (rehome) return null;

  const [docModule, docScreen] = (doc ?? '').split('.') as [ModuleKey | undefined, string | undefined];
  const def = MODULES_BY_KEY[module];
  const screenDef = def?.menus.flatMap((m) => m.screens).find((s) => s.key === screen);
  if (!def || !screenDef) return <NotFound />;
  if (!canOpenScreen(perms, module, screen)) return <Forbidden what={screenDef.label} />;
  if (kind === 'detail' && docModule && docScreen) {
    const hosted = MODULE_UI[docModule]?.screens?.[docScreen]?.detail;
    const hostedDef = MODULES_BY_KEY[docModule]?.menus.flatMap((m) => m.screens).find((s) => s.key === docScreen);
    if (!hosted || !hostedDef) return <NotFound />;
    if (!canDo(perms, docModule, docScreen, 'view')) return <Forbidden what={hostedDef.label} />;
    const Hosted = hosted;
    return <div key={`${module}/${screen}/${doc}/${id}/${fresh}/${viewOnly}`} className="fx-rise">{viewOnly ? <ViewOnly canEdit={canDo(perms, docModule, docScreen, 'edit')}><Hosted /></ViewOnly> : <Hosted />}</div>;
  }

  const impl = MODULE_UI[module].screens?.[screen];
  const Component = kind === 'detail' ? impl?.detail : impl?.list;
  if (Component) return <div key={`${module}/${screen}/${kind}/${fresh}/${viewOnly}`} className="fx-rise">{viewOnly ? <ViewOnly canEdit={canDo(perms, module, screen, 'edit')}><Component /></ViewOnly> : <Component />}</div>;
  if (kind === 'detail' && impl) return <NotFound />;

  return (
    <>
      <PageHeader title={screenDef.label} description={`${def.name} › ${def.menus.find((m) => m.screens.includes(screenDef))?.label}`} />
      <Card>
        <EmptyState
          icon={<Construction />}
          title="Screen reserved"
          description="This screen's navigation, permissions and route are in place. It will be built when we cover this part of the existing system."
        />
      </Card>
    </>
  );
}

/** A record opened "view only": nothing on it can be changed; Edit (for those allowed) opens it for editing. */
function ViewOnly({ canEdit, children }: { canEdit: boolean; children: ReactNode }) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  return (
    <ViewOnlyContext.Provider value>
      <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-primary/30 bg-primary-soft px-3 py-2 text-[13px] text-primary">
        <Eye className="size-4" />
        <span className="font-medium">View only</span>
        <span className="text-fg-muted">Nothing here can be changed.</span>
        {canEdit && (
          <Button size="sm" className="ml-auto" onClick={() => void navigate({ to: '.', search: { ...search, mode: undefined } as never, replace: true })}>
            <Pencil className="size-3.5" />
            Edit
          </Button>
        )}
      </div>
      {children}
    </ViewOnlyContext.Provider>
  );
}
